/**
 * The Employee Lifeline (queue item 219) -- one person's record, opened from an employee in
 * the portal (a finding's rows, the Register). Direction A, the metro-map lifeline, is the
 * main view (one vertical line on a phone); B, pay strata, is the drill-in from the Pay
 * line; C, the year spiral, is the Patterns tab. Data: GET /api/portal/{customer}/employees/
 * {number}/lifeline -- every section present, each with data or the reason it has none.
 * Served through the customer view: approved findings only, no SQL; read-only in a preview.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { getEmployeeLifeline } from '../../../api'
import LoadError from '../../../components/LoadError'
import LifelineMap from './LifelineMap'
import LifelineTable from './LifelineTable'
import LifelineVertical from './LifelineVertical'
import PayStrata from './PayStrata'
import PreHire from './PreHire'
import YearSpiral from './YearSpiral'
import { Tip, TipProvider } from './Tip'
import { ATTN_HELP, HowToKey, LifelineToolbar } from './Guidance'
import { SevIcon } from './Marks'
import { ReasonText } from './LifelineTable'
import { changeText, fmtDate, fmtMoney, fmtPay, findingMarkers, ordinal, peerSummary, preHireEvents, stations as toStations, tenure, viewTabs } from './lifelineModel'
import './Lifeline.css'

function useIsPhone() {
  const q = '(max-width: 720px)'
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(q).matches)
  useEffect(() => {
    const mq = window.matchMedia?.(q)
    if (!mq) return undefined
    const on = () => setPhone(mq.matches)
    mq.addEventListener?.('change', on)
    return () => mq.removeEventListener?.('change', on)
  }, [])
  return phone
}

// While the customer's findings are still being read the server says so (pending); the
// page asks again on its own, a few times, a few seconds apart.
const PENDING_RETRY_MS = 3000
const PENDING_RETRIES = 20

function NotAvailable({ section, what }) {
  return <p className="ll-na"><b>{what} not available.</b> {section?.reason || 'No data.'}</p>
}

function Evidence({ selected, markers, stationsList, onOpenCheck, findings }) {
  if (selected?.type === 'station') {
    const st = stationsList.find(s => s.id === selected.id)
    if (st) {
      return (
        <section className="ll-card ll-card--wide" aria-live="polite">
          <div className="ll-eyebrow">{fmtDate(st.date)}</div>
          <h2>{st.title}{st.interchange ? ` · ${st.changes.length} fields in one transaction` : ''}</h2>
          {(st.reason || st.reason_code) && <div className="ll-note">Reason: <ReasonText reason={st.reason} code={st.reason_code} /></div>}
          {st.changes?.length
            ? <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.6 }}>{st.changes.map((c, i) => <li key={i}>{changeText(c)}</li>)}</ul>
            : <p className="ll-na">The first record of this person's job history.</p>}
        </section>
      )
    }
  }
  if (!findings?.available) {
    return <section className="ll-card ll-card--wide"><div className="ll-eyebrow">Findings</div><NotAvailable section={findings} what="Findings" /></section>
  }
  const m = markers.find(x => selected?.type === 'finding' && x.id === selected.id) || markers[0]
  if (!m) {
    return (
      <section className="ll-card ll-card--wide">
        <div className="ll-eyebrow">Findings</div>
        <h2>No open findings name this person</h2>
        <p className="ll-na">Checked {findings.checked} of {findings.active_findings} open findings.{findings.note ? ` ${findings.note}` : ''}</p>
      </section>
    )
  }
  const f = m.finding
  const cols = (f.columns || []).filter(c => f.rows.some(r => r[c] != null)).slice(0, 8)
  return (
    <section className="ll-card ll-card--wide" aria-live="polite">
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className={`ll-sev ll-sev--${m.severity}`}>{m.severity}</span>
        <span className="ll-eyebrow">Evidence · {f.status || 'open'}</span>
      </div>
      <h2>{f.title}</h2>
      {f.detail && <p className="ll-na" style={{ color: 'var(--gray-700)' }}>{f.detail}</p>}
      {m.check && (
        <div className="ll-note">Points at the check paid {fmtDate(m.check.pay_date)} · gross {fmtMoney(m.check.gross)}{m.check.state ? ` · ${m.check.state}` : ''}</div>
      )}
      <div className="ll-table-wrap">
        <table className="ll-table ll-mono">
          <thead><tr>{cols.map(c => <th key={c}>{c.replace(/_/g, ' ')}</th>)}</tr></thead>
          <tbody>{f.rows.map((r, i) => <tr key={i}>{cols.map(c => <td key={c}>{r[c] != null ? String(r[c]) : '—'}</td>)}</tr>)}</tbody>
        </table>
      </div>
      {f.row_count > f.rows.length && <div className="ll-note">Showing {f.rows.length} of {f.row_count} rows about this person.</div>}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {m.check && <button type="button" className="ll-link" onClick={() => onOpenCheck(m.check.pay_identifier)}>Open the check in pay strata</button>}
      </div>
    </section>
  )
}

function Peers({ peers, on }) {
  if (!peers?.available) {
    return <section className="ll-card"><div className="ll-eyebrow" style={{ color: 'var(--ll-peer-line)' }}>Compare to peers</div><NotAvailable section={peers} what="Peer comparison" /></section>
  }
  // like with like: hourly rates among hourly people, annual salaries among salaried people
  const metric = peers.metric || 'hourly_rate'
  const r = peers[metric]
  const lo = Math.min(r.p25, r.value) * 0.95, hi = Math.max(r.p75, r.value) * 1.05
  const pct = v => `${((v - lo) / (hi - lo || 1)) * 100}%`
  return (
    <section className="ll-card">
      <div className="ll-eyebrow" style={{ color: 'var(--ll-peer-line)' }}>Compare to peers</div>
      <h2>Same job and work location{peers.basis ? `, ${peers.basis}` : ''}</h2>
      {!on && <p className="ll-na">Turn on Compare to peers to draw the band on the map and the strata.</p>}
      <div className="ll-note">{peers.n} peers · bar = p25 to p75 · tick = median · dot = this person</div>
      <div className="ll-peer-row">
        <div className="ll-peer-head"><span>{metric === 'annual_salary' ? 'Annual salary' : 'Hourly rate'}</span><span className="ll-mono"><b>{fmtPay(r.value, metric)}</b> <span className="ll-note">vs {fmtPay(r.median, metric)}</span></span></div>
        <div className="ll-peer-track" aria-hidden="true">
          <span style={{ left: 0, right: 0, top: 6, height: 2, background: 'var(--ll-rule)' }} />
          <span style={{ left: pct(r.p25), width: `calc(${pct(r.p75)} - ${pct(r.p25)})`, top: 3, height: 8, background: 'var(--ll-peer)', borderRadius: 2 }} />
          <span style={{ left: pct(r.median), top: 0, width: 2, height: 14, background: 'var(--ll-peer-line)' }} />
          <span style={{ left: pct(r.value), top: 2, width: 10, height: 10, marginLeft: -5, borderRadius: '50%', background: 'var(--ll-ink)', boxShadow: '0 0 0 2px #fff' }} />
        </div>
        <div className="ll-note">{peerSummary(r, peers.n) || `${ordinal(r.percentile)} percentile of peers`}</div>
      </div>
    </section>
  )
}

export default function EmployeeLifeline({ customerId, employeeRef, onClose, onPickAnother = null }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [attempt, setAttempt] = useState(0)
  const [view, setView] = useState('map')            // map | table | patterns | pay
  const [hidden, setHidden] = useState(() => new Set())
  const [peersOn, setPeersOn] = useState(false)
  const [selected, setSelected] = useState(null)
  const [selectedCheck, setSelectedCheck] = useState(null)
  const [howTo, setHowTo] = useState(false)
  const howToLink = useRef(null)
  const howToHead = useRef(null)
  const phone = useIsPhone()
  const uid = useId()

  const [refresh, setRefresh] = useState(0)
  useEffect(() => {
    let live = true
    if (refresh === 0) { setData(null); setError(null) }
    getEmployeeLifeline(customerId, employeeRef)
      .then(d => { if (live) setData(d) })
      .catch(e => { if (live && refresh === 0) setError(e) })
    return () => { live = false }
  }, [customerId, employeeRef, attempt, refresh])
  const findingsPending = !!data?.sections?.findings?.pending
  useEffect(() => {
    if (!findingsPending || refresh >= PENDING_RETRIES) return undefined
    const t = setTimeout(() => setRefresh(n => n + 1), PENDING_RETRY_MS)
    return () => clearTimeout(t)
  }, [findingsPending, refresh])

  const s = data?.sections || {}
  const stationsList = useMemo(() => (s.events?.available ? toStations(s.events.items) : []), [data])
  const preEvents = useMemo(() => preHireEvents(s.pre_hire), [data])
  const findings = s.findings
  const markers = useMemo(() => findingMarkers(findings?.available ? findings.items : [],
    s.pay_statements?.available ? s.pay_statements.checks : []), [data])
  const openCheck = useCallback((pid) => { setSelectedCheck(pid); setView('pay') }, [])
  // the key: its heading takes focus when it opens; Escape or its close button shuts it and
  // focus goes back to the link that opened it
  const closeHowTo = useCallback(() => {
    setHowTo(false)
    requestAnimationFrame(() => howToLink.current?.focus())
  }, [])
  useEffect(() => {
    if (!howTo) return undefined
    howToHead.current?.focus()
    const key = e => { if (e.key === 'Escape') closeHowTo() }
    document.addEventListener('keydown', key)
    return () => document.removeEventListener('keydown', key)
  }, [howTo, closeHowTo])
  const toggleLine = (k) => setHidden(prev => { const n = new Set(prev); n.has(k) ? n.delete(k) : n.add(k); return n })

  const back = (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <button type="button" className="ll-back" onClick={onClose}>← Back to the portal</button>
      {onPickAnother && <button type="button" className="ll-back" onClick={onPickAnother}>Choose another employee</button>}
    </div>
  )
  if (error) {
    return <div className="ll-root">{back}<LoadError what="this employee's lifeline" error={error} onRetry={() => setAttempt(n => n + 1)} /></div>
  }
  if (!data) {
    return <div className="ll-root">{back}<div className="ll-loading" role="status"><div className="dm-spinner" />Loading the lifeline…</div></div>
  }

  const p = data.person
  const name = [p.preferred_name || p.first_name, p.last_name].filter(Boolean).join(' ') || `Employee ${p.employee_number || ''}`.trim()
  const attention = markers.slice(0, 4)
  const tabs = viewTabs(phone)

  return (
    <TipProvider>
    <div className="ll-root">
      {back}
      <header className="ll-header">
        <div className="ll-who">
          <div className="ll-eyebrow">{[p.company_name, p.employee_number ? `Employee ${p.employee_number}` : null, p.company_code].filter(Boolean).join(' · ')}</div>
          <h1 className="ll-name">{name}</h1>
          <div className="ll-facts">
            {p.job_title && <span><b>{p.job_title}</b>{p.job_code ? ` · ${p.job_code}` : ''}</span>}
            {p.manager && <span>Manager <b>{p.manager}</b></span>}
            {p.pay_type && <span className="ll-mono">{p.pay_type}{p.full_or_part_time ? ` · ${p.full_or_part_time === 'F' ? 'full time' : p.full_or_part_time === 'P' ? 'part time' : p.full_or_part_time}` : ''}</span>}
            {p.last_hire_date && <span>Hired {fmtDate(p.last_hire_date)}{tenure(p.last_hire_date, p.termination_date || data.as_of) ? ` · ${tenure(p.last_hire_date, p.termination_date || data.as_of)}` : ''}{p.original_hire_date && p.original_hire_date !== p.last_hire_date ? ` · first hired ${fmtDate(p.original_hire_date)}` : ''}</span>}
            {p.status?.code && <span className={`ll-pill ll-pill--${p.status.code}`}><span className="ll-pill-dot" />{p.status.label}</span>}
            {p.termination_date && <span>Terminated {fmtDate(p.termination_date)}{p.termination_reason ? ` · ${p.termination_reason}` : ''}</span>}
          </div>
        </div>
        <div className="ll-attn">
          <div className="ll-eyebrow">What needs attention</div>
          <div className="ll-attn-list">
            {!findings?.available && <span className="ll-attn-none" role={findings?.pending ? 'status' : undefined}>{findings?.reason}{findings?.pending && findings.active_findings ? ` (${findings.read} of ${findings.active_findings})` : ''}</span>}
            {findings?.available && !attention.length && <span className="ll-attn-none">No open findings name this person.</span>}
            {attention.map(m => (
              <Tip key={m.id} text={ATTN_HELP}>
                <button type="button" className={`ll-attn-chip ll-attn-chip--${m.severity}`}
                        onClick={() => { setSelected({ type: 'finding', id: m.id }); if (view !== 'map') setView('map') }}>
                  <SevIcon tier={m.severity} /><span>{m.finding.title}</span>
                </button>
              </Tip>
            ))}
          </div>
        </div>
      </header>

      <LifelineToolbar tabs={tabs} view={view} onView={setView} phone={phone} showChips={!phone && view === 'map'}
                       hidden={hidden} onToggleLine={toggleLine} peers={s.peers} peersOn={peersOn} onPeers={setPeersOn}
                       howTo={howTo} onHowTo={setHowTo} howToRef={howToLink} asOf={data.as_of} chipsLabelId={`${uid}-lines`} />
      {howTo && <HowToKey phone={phone} onClose={closeHowTo} headingRef={howToHead} />}

      {view === 'map' && !phone && (
        s.events?.available || s.pay?.available
          ? <LifelineMap data={data} stations={stationsList} markers={markers} hidden={hidden} peersOn={peersOn}
                         selected={selected} onSelect={setSelected} onOpenPay={() => setView('pay')} />
          : <section className="ll-card"><NotAvailable section={s.events} what="The map" /></section>
      )}
      {((view === 'map' && phone) || view === 'story') && (
        <section className={`ll-card${phone ? '' : ' ll-story'}`}><LifelineVertical data={data} stations={stationsList} markers={markers} preEvents={preEvents} onSelect={setSelected} /></section>
      )}
      {view === 'table' && <LifelineTable stationsList={stationsList} preEvents={preEvents} events={s.events} markers={markers} onSelect={setSelected} />}
      {view === 'pay' && (
        <PayStrata customerId={customerId} data={data} markers={markers} selectedCheck={selectedCheck}
                   onSelectCheck={setSelectedCheck} peersOn={peersOn} />
      )}
      {view === 'patterns' && <YearSpiral data={data} stations={stationsList} markers={markers} />}

      {view !== 'patterns' && (
        <div className="ll-row">
          <Evidence selected={selected} markers={markers} stationsList={stationsList} onOpenCheck={openCheck} findings={findings} />
          <Peers peers={s.peers} on={peersOn} />
        </div>
      )}
      {view !== 'patterns' && <PreHire section={s.pre_hire} />}
      {findings?.available && findings.note && <p className="ll-note">{findings.note}</p>}
    </div>
    </TipProvider>
  )
}
